import { useState, type ReactNode } from 'react'
import { useLanguage } from '../contexts/LanguageContext'
import { cardGrid } from '../lib/ui'

/**
 * カードを並べ替えられるグリッド。
 *
 * 並べ替え中はカード面の操作（編集・取引一覧へのリンク）を止め、ドラッグ＆ドロップと
 * ←/→ ボタンの両方で動かせるようにする。タッチ端末では drag イベントが飛ばないので、
 * ドラッグだけにするとスマートフォンから並べ替えられなくなる。
 *
 * ドラッグの状態はこのグリッドの中だけで持つため、別のグリッド（別の所有者・別の種別の
 * まとまり）へ落としても何も起きない。並べ替えは同じまとまりの中に限られる。
 */
export function SortableCardGrid<T extends { id: string }>({
  items,
  sorting,
  onMove,
  children,
}: {
  items: T[]
  /** 並べ替えモード。false のときは素のグリッドとして描くだけ */
  sorting: boolean
  /** moving を target の位置へ動かす */
  onMove: (movingId: string, targetId: string) => void
  children: (item: T) => ReactNode
}) {
  const { t } = useLanguage()
  const [draggingId, setDraggingId] = useState<string | null>(null)
  const [overId, setOverId] = useState<string | null>(null)

  const endDrag = () => {
    setDraggingId(null)
    setOverId(null)
  }

  if (!sorting) {
    return (
      <div className={`mt-3 ${cardGrid}`}>
        {items.map((item) => (
          <div key={item.id}>{children(item)}</div>
        ))}
      </div>
    )
  }

  return (
    <div className={`mt-3 ${cardGrid}`}>
      {items.map((item, i) => (
        <div
          key={item.id}
          draggable
          onDragStart={(e) => {
            setDraggingId(item.id)
            e.dataTransfer.effectAllowed = 'move'
            // Firefox はデータが空だとドラッグを始めないため、ダミーでも入れておく
            e.dataTransfer.setData('text/plain', item.id)
          }}
          onDragEnd={endDrag}
          onDragOver={(e) => {
            if (!draggingId || draggingId === item.id) return
            e.preventDefault()
            e.dataTransfer.dropEffect = 'move'
            setOverId(item.id)
          }}
          onDragLeave={() => setOverId((cur) => (cur === item.id ? null : cur))}
          onDrop={(e) => {
            e.preventDefault()
            if (draggingId) onMove(draggingId, item.id)
            endDrag()
          }}
          className={`cursor-grab rounded-xl border-2 p-2 transition-colors active:cursor-grabbing ${
            draggingId === item.id
              ? 'border-indigo-400 opacity-40'
              : overId === item.id
                ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950'
                : 'border-dashed border-gray-300 dark:border-gray-700'
          }`}
        >
          {/* ボタンはカードの上に置く。カード面に重ねると口座名や種別を隠してしまう */}
          <div className="mb-2 flex items-center gap-1">
            <button
              type="button"
              onClick={() => onMove(item.id, items[i - 1].id)}
              disabled={i === 0}
              title={t('accounts_reorder_move_prev')}
              aria-label={t('accounts_reorder_move_prev')}
              className={moveButton}
            >
              &#8592;
            </button>
            <button
              type="button"
              onClick={() => onMove(item.id, items[i + 1].id)}
              disabled={i === items.length - 1}
              title={t('accounts_reorder_move_next')}
              aria-label={t('accounts_reorder_move_next')}
              className={moveButton}
            >
              &#8594;
            </button>
            <span className="ml-auto pr-1 text-gray-400 dark:text-gray-500" aria-hidden="true">&#9783;</span>
          </div>
          {/* 並べ替え中はカード面を押せないようにする。リンクや編集ボタンが反応すると、
              動かそうとしただけで取引一覧へ飛んでしまう */}
          <div className="pointer-events-none select-none">{children(item)}</div>
        </div>
      ))}
    </div>
  )
}

/** 前へ/後ろへ動かすボタン。色付きのカード面の上でも読めるよう、白地で浮かせる */
const moveButton =
  'flex h-7 w-7 items-center justify-center rounded-full border border-indigo-200 bg-white/95 text-sm text-indigo-600 shadow-sm transition-colors hover:bg-indigo-50 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-white/95 dark:border-indigo-800 dark:bg-gray-900/95 dark:text-indigo-300 dark:hover:bg-gray-800'
